import React, { useState, useEffect } from 'react'
import type { FileAnalysis } from '../../lib/smartDropHandler'
import './MetadataWizard.css'

interface MetadataWizardProps {
    isOpen: boolean
    onClose: () => void
    onSubmit: (metadata: BatchWitnessMetadata) => void
    fileAnalyses: FileAnalysis[]
}

export interface WitnessFileData {
    file: File
    chapter: string
    siglum: string
    library: string
    description: string
    isValid: boolean
    error?: string
}

export interface BatchWitnessMetadata {
    bookTitle: string
    chapterLatin: string
    chapterArabic: string
    witnesses: WitnessFileData[]
}

const MetadataWizard: React.FC<MetadataWizardProps> = ({ isOpen, onClose, onSubmit, fileAnalyses }) => {
    const [bookTitle, setBookTitle] = useState('')
    const [chapterLatin, setChapterLatin] = useState('')
    const [chapterArabic, setChapterArabic] = useState('')
    const [witnesses, setWitnesses] = useState<WitnessFileData[]>([])

    // Initialize witnesses from file analyses
    useEffect(() => {
        if (fileAnalyses.length > 0) {
            const initialWitnesses: WitnessFileData[] = fileAnalyses.map((analysis) => {
                const meta = analysis.inferredMetadata
                return {
                    file: analysis.file,
                    chapter: meta?.chapter || '',
                    siglum: meta?.siglum || '',
                    library: meta?.library || '',
                    description: meta?.description || meta?.siglum || '',
                    isValid: meta?.parsedData?.isValid || false,
                    error: meta?.parsedData?.error,
                }
            })
            setWitnesses(initialWitnesses)
        }
    }, [fileAnalyses])

    if (!isOpen) return null

    const handleSubmit = () => {
        if (bookTitle && chapterLatin && chapterArabic && witnesses.every((w) => w.siglum)) {
            onSubmit({
                bookTitle,
                chapterLatin,
                chapterArabic,
                witnesses,
            })
        }
    }

    const updateWitness = (index: number, field: keyof WitnessFileData, value: string) => {
        setWitnesses((prev) =>
            prev.map((w, i) =>
                i === index
                    ? {
                        ...w,
                        [field]: value,
                        isValid: field === 'siglum' || field === 'chapter' ? true : w.isValid,
                    }
                    : w
            )
        )
    }

    const validWitnessCount = witnesses.filter((w) => w.siglum && w.chapter).length
    const totalCount = witnesses.length

    return (
        <div className="wizard-overlay" onClick={onClose}>
            <div className="wizard-modal wizard-modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="wizard-title">
                    We see you dropped {totalCount} file{totalCount > 1 ? 's' : ''}
                </div>
                <div className="wizard-subtitle">Let's set up your workspace</div>

                <div className="wizard-form">
                    {/* Book & Chapter Metadata */}
                    <div className="wizard-form-row">
                        <div className="wizard-form-group">
                            <label className="wizard-form-label">Book Title</label>
                            <input
                                type="text"
                                className="wizard-form-input"
                                placeholder="e.g., Kalīla wa Dimna"
                                value={bookTitle}
                                onChange={(e) => setBookTitle(e.target.value)}
                            />
                        </div>

                        <div className="wizard-form-group">
                            <label className="wizard-form-label">Chapter (Latin)</label>
                            <input
                                type="text"
                                className="wizard-form-input"
                                placeholder="e.g., The Lion and the Ox"
                                value={chapterLatin}
                                onChange={(e) => setChapterLatin(e.target.value)}
                            />
                        </div>

                        <div className="wizard-form-group">
                            <label className="wizard-form-label">Chapter (Arabic)</label>
                            <input
                                type="text"
                                className="wizard-form-input"
                                placeholder="e.g., باب الأسد والثور"
                                dir="rtl"
                                value={chapterArabic}
                                onChange={(e) => setChapterArabic(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Witnesses Preview Table */}
                    <div className="wizard-preview-section">
                        <h3 className="wizard-section-title">
                            Witness Files ({validWitnessCount}/{totalCount} ready)
                        </h3>
                        <div className="wizard-table-container">
                            <table className="wizard-table">
                                <thead>
                                    <tr>
                                        <th>Filename</th>
                                        <th>Chapter</th>
                                        <th>Siglum</th>
                                        <th>Library</th>
                                        <th>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {witnesses.map((witness, index) => (
                                        <tr key={index} className={witness.isValid ? 'valid' : 'invalid'}>
                                            <td className="filename-cell" title={witness.file.name}>
                                                {witness.file.name}
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="wizard-table-input"
                                                    value={witness.chapter}
                                                    onChange={(e) => updateWitness(index, 'chapter', e.target.value)}
                                                    placeholder="Chapter"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="wizard-table-input"
                                                    value={witness.siglum}
                                                    onChange={(e) => updateWitness(index, 'siglum', e.target.value)}
                                                    placeholder="Siglum"
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="wizard-table-input"
                                                    value={witness.library}
                                                    onChange={(e) => updateWitness(index, 'library', e.target.value)}
                                                    placeholder="Library"
                                                />
                                            </td>
                                            <td className="status-cell">
                                                {witness.isValid ? (
                                                    <span className="status-valid">✓ Valid</span>
                                                ) : (
                                                    <span className="status-invalid" title={witness.error}>
                                                        ⚠ Review
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="wizard-actions">
                    <button className="wizard-btn wizard-btn-ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="wizard-btn wizard-btn-primary"
                        onClick={handleSubmit}
                        disabled={!bookTitle || !chapterLatin || !chapterArabic || validWitnessCount === 0}
                    >
                        Create {validWitnessCount} Witness{validWitnessCount !== 1 ? 'es' : ''}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default MetadataWizard
